import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { FormDatePicker } from '@/components/form/FormDatePicker'
import { FormErrorSummary } from '@/components/form/FormErrorSummary'
import { FormInput } from '@/components/form/FormInput'
import { FormSelect } from '@/components/form/FormSelect'
import { FormTextarea } from '@/components/form/FormTextarea'
import { Button } from '@/components/ui/Button'
import type { StudentProfile } from '@/features/student/api/profile.api'
import { useUpdateProfile } from '@/features/student/hooks/useProfile'
import { getErrorMessage, getFieldErrors } from '@/utils/errors'
import { humanizeEnum } from '@/utils/format'

const GENDER_OPTIONS = ['male', 'female', 'other', 'prefer_not_to_say'].map((value) => ({
  value,
  label: humanizeEnum(value),
}))

const WORK_MODE_OPTIONS = ['onsite', 'remote', 'hybrid'].map((value) => ({
  value,
  label: humanizeEnum(value),
}))

const schema = z.object({
  headline: z.string().max(255).optional(),
  career_summary: z.string().max(5000).optional(),
  dob: z.string().optional(),
  gender: z.string().optional(),
  marital_status: z.string().max(30).optional(),
  current_city: z.string().max(100).optional(),
  current_state: z.string().max(100).optional(),
  current_country: z.string().max(100).optional(),
  preferred_work_mode: z.string().optional(),
})

type FormValues = z.infer<typeof schema>

function toFormValues(profile: StudentProfile): FormValues {
  return {
    headline: profile.headline ?? '',
    career_summary: profile.careerSummary ?? '',
    dob: profile.dob ?? '',
    gender: profile.gender ?? '',
    marital_status: profile.maritalStatus ?? '',
    current_city: profile.currentCity ?? '',
    current_state: profile.currentState ?? '',
    current_country: profile.currentCountry ?? '',
    preferred_work_mode: profile.preferredWorkMode ?? '',
  }
}

export function BasicDetailsForm({
  profile,
  onSaved,
}: {
  profile: StudentProfile
  onSaved?: () => void
}) {
  const update = useUpdateProfile()
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: toFormValues(profile) })

  const onSubmit = handleSubmit((values) => {
    const payload = {
      ...values,
      gender: values.gender || undefined,
      preferred_work_mode: values.preferred_work_mode || undefined,
    }
    update.mutate(payload, { onSuccess: onSaved })
  })

  return (
    <form onSubmit={(e) => void onSubmit(e)} noValidate>
      <FormErrorSummary
        errors={errors}
        serverErrors={getFieldErrors(update.error)}
        generalMessage={update.error ? getErrorMessage(update.error) : null}
      />
      <FormInput
        control={control}
        name="headline"
        label="Headline"
        placeholder="e.g. Aspiring Software Engineer"
      />
      <FormTextarea
        control={control}
        name="career_summary"
        label="Career Summary"
        maxLength={5000}
      />
      <div className="row">
        <div className="col-md-6">
          <FormDatePicker control={control} name="dob" label="Date of Birth" />
        </div>
        <div className="col-md-6">
          <FormSelect control={control} name="gender" label="Gender" options={GENDER_OPTIONS} />
        </div>
      </div>
      <FormInput control={control} name="marital_status" label="Marital Status" />
      <div className="row">
        <div className="col-md-4">
          <FormInput control={control} name="current_city" label="City" />
        </div>
        <div className="col-md-4">
          <FormInput control={control} name="current_state" label="State" />
        </div>
        <div className="col-md-4">
          <FormInput control={control} name="current_country" label="Country" />
        </div>
      </div>
      <FormSelect
        control={control}
        name="preferred_work_mode"
        label="Preferred Work Mode"
        options={WORK_MODE_OPTIONS}
      />
      <Button type="submit" loading={update.isPending}>
        Save
      </Button>
    </form>
  )
}
